/* Lux cart plans engine (spec 2026-07-10). One document-level engine drives
   every [data-lux-cart-plans] root (drawer + cart page can coexist on /cart).
   State single-source = sessionStorage.luxCartPlan ({mode, months}); absent or
   blocked = cash (safety rail 2). The drawer swaps its innerHTML on every cart
   change (cart-drawer.js renderContents), so events are DELEGATED on document
   and a MutationObserver re-applies the stored state after each swap. Math =
   lux-pdp.js engine verbatim (final rule 2026-07-07). */
(() => {
  if (window.luxCartPlans) return;
  window.luxCartPlans = true;
  const MIN_MONTHS = 6, MAX_MONTHS = 12, MIN_MONTHLY = 4000;
  /* Offered terms (Tsoulfa 2026-07-18): 6, 9, 12 ONLY. Descending for snap-down. */
  const INST_TERMS = [12, 9, 6];
  const snapTerm = (cap) => INST_TERMS.find((m) => m <= cap) || 0;
  const fmtEUR = (cents) => {
    const whole = Math.round(cents) / 100;
    const opts = Number.isInteger(whole)
      ? { maximumFractionDigits: 0 }
      : { minimumFractionDigits: 2, maximumFractionDigits: 2 };
    return "€" + whole.toLocaleString("el-GR", opts);
  };
  const t = (key, repl) => {
    let s = (window.luxCartPlanStrings && window.luxCartPlanStrings[key]) || "";
    Object.entries(repl || {}).forEach(([k, val]) => { s = s.replace(`{{ ${k} }}`, val); });
    return s;
  };
  /* Write only on real change: repaint runs after every cart mutation and on
     every plan click, and unconditional writes re-announce identical values
     through the aria-live receipts and feed mutation storms on spam clicks. */
  const setText = (el, s) => { if (el && el.textContent !== s) el.textContent = s; };
  const readHint = () => {
    try { return JSON.parse(sessionStorage.getItem("luxCartPlan") || "null"); } catch { return null; }
  };
  const saveHint = (mode, months) => {
    try {
      /* cash-1 = the default state, no hint needed; cash-2/3 (split) and inst
         persist so the aitisi page preselects the same plan. */
      if (mode === "cash" && (!months || months <= 1)) sessionStorage.removeItem("luxCartPlan");
      else sessionStorage.setItem("luxCartPlan", JSON.stringify({ mode, months }));
    } catch { /* storage blocked: stays cash on next paint */ }
  };
  const dataOf = (root) => {
    const el = root.querySelector("script[data-lux-cart-data]");
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch { return null; }
  };
  const planFor = (reg) => {
    const deposit = Math.round(reg * 0.2 / 100) * 100;
    const maxMonths = snapTerm(Math.min(MAX_MONTHS, Math.floor((reg - deposit) / MIN_MONTHLY)));
    return { deposit, maxMonths };
  };
  let drawerEditorOpen = false;
  const drawerEditorMedia = window.matchMedia("(max-width: 899px), (max-height: 850px)");
  const initializedEditors = new WeakSet();
  document.addEventListener("toggle", (e) => {
    /* Open server markup also emits toggle, before DOM ready or the repaint
       after a section swap. Only initialized editors carry shopper intent. */
    if (drawerEditorMedia.matches && initializedEditors.has(e.target) && e.target.matches("[data-lux-cart-editor]") && e.target.closest("[data-lux-cart-plans]")?.dataset.context === "drawer") {
      drawerEditorOpen = e.target.open;
    }
  }, true);
  drawerEditorMedia.addEventListener("change", () => {
    document.querySelectorAll('[data-lux-cart-plans][data-context="drawer"] [data-lux-cart-editor]').forEach((editor) => {
      if (!drawerEditorMedia.matches && initializedEditors.has(editor)) drawerEditorOpen = editor.open;
      editor.open = drawerEditorMedia.matches ? drawerEditorOpen : true;
    });
  });
  const paint = (root) => {
    const d = dataOf(root);
    if (!d) return;
    const { deposit, maxMonths } = planFor(d.reg);
    const hint = readHint();
    const inst = !!(hint && hint.mode === "inst");
    const months = inst && INST_TERMS.includes(hint.months) && hint.months <= maxMonths ? hint.months : maxMonths;
    root.querySelectorAll("[data-lux-cart-mode-input]").forEach((r) => { r.checked = (r.value === "inst") === inst; });
    const selectedCard = root.querySelector('[data-lux-cart-mode-input]:checked')?.closest(".lux-cart-plans__opt");
    setText(root.querySelector("[data-lux-cart-selected-label]"), selectedCard?.querySelector(".lux-cart-plans__opt-name")?.textContent.trim() || "");
    const editor = root.querySelector("[data-lux-cart-editor]");
    if (editor && root.dataset.context === "drawer" && !initializedEditors.has(editor)) {
      /* Keep the receipt visible; editing controls collapse in compact views.
         Cart refreshes must not undo a shopper's open/closed choice. */
      editor.open = drawerEditorMedia.matches ? drawerEditorOpen : true;
      initializedEditors.add(editor);
    }
    root.querySelectorAll("[data-lux-cart-months] .lux-plans__pill").forEach((pill) => {
      const input = pill.querySelector("input");
      const val = parseInt(input.value, 10);
      pill.hidden = val > maxMonths;
      const on = val === months;
      input.checked = on;
      pill.classList.toggle("lux-plans__pill--active", on);
    });
    const monthly = Math.floor((d.reg - deposit) / months);
    setText(root.querySelector("[data-lux-cart-teaser]"), fmtEUR(monthly));
    /* Receipt rows (Tsoulfa 2026-07-11): deposit / monthly / total, aligned. */
    setText(root.querySelector("[data-lux-cart-val-deposit]"), fmtEUR(deposit));
    setText(root.querySelector("[data-lux-cart-val-monthly]"), months + " × " + fmtEUR(monthly));
    setText(root.querySelector("[data-lux-cart-val-total]"), fmtEUR(d.reg));
    const apply = root.querySelector("[data-lux-cart-apply]:not([data-lux-cash-apply])");
    if (apply) apply.href = apply.href.split("?")[0] + "?cart=1&months=" + months;
    /* Cash detail (Tsoulfa 2026-07-11, PDP logic): 1 = plain checkout with just
       the total; 2/3 = split cash, payments row + apply CTA (aitisi reads the
       cash hint from sessionStorage, so its link carries no months param). */
    const cashN = hint && hint.mode === "cash" && hint.months >= 2 && hint.months <= 3 ? hint.months : 1;
    root.querySelectorAll("[data-lux-cart-cash] .lux-plans__pill").forEach((pill) => {
      const input = pill.querySelector("input");
      const on = parseInt(input.value, 10) === cashN;
      input.checked = on;
      pill.classList.toggle("lux-plans__pill--active", on);
    });
    const cashPayRow = root.querySelector("[data-lux-cash-row-payments]");
    if (cashPayRow) cashPayRow.hidden = cashN <= 1;
    setText(root.querySelector("[data-lux-cash-val-payments]"), cashN + " × " + fmtEUR(Math.floor(d.cash / cashN)));
    setText(root.querySelector("[data-lux-cash-val-total]"), fmtEUR(d.cash));
    const cashApply = root.querySelector("[data-lux-cash-apply]");
    if (cashApply) cashApply.hidden = cashN <= 1;
  };
  const paintAll = () => document.querySelectorAll("[data-lux-cart-plans]").forEach(paint);
  /* Checkout label quiets to «ή ολοκλήρωσε με μετρητοίς» while δόσεις is
     selected; original innerHTML (incl. the lock icon) is restored on cash.
     Look is CSS-driven either way, so no-JS stays safe with the original label. */
  const syncCheckoutLabels = () => {
    const h = readHint();
    /* Quiet the checkout button for inst AND for cash-split (2/3 payments both
       run through the application; instant checkout would charge the full cash
       amount in one payment). ALWAYS require a live block (final review blocker
       2): if the basket dropped below eligibility the block is gone, and a
       stale hint must not relabel the only checkout CTA. */
    const alive = !!document.querySelector("[data-lux-cart-inst]");
    const inst = !!(h && h.mode === "inst") && alive;
    const cashSplit = !!(h && h.mode === "cash" && h.months >= 2) && alive;
    document.querySelectorAll("#CartDrawer-Checkout, .cart__checkout").forEach((btn) => {
      if (!btn.luxOriginalHtml) btn.luxOriginalHtml = btn.innerHTML;
      /* A missing first-add translation must never erase the checkout action.
         Both alternatives still lead to the full cash checkout. */
      const label = inst ? t("or_cash") : cashSplit ? t("or_cash_full") || t("or_cash") : "";
      if (label) setText(btn, label);
      else if (btn.innerHTML !== btn.luxOriginalHtml) btn.innerHTML = btn.luxOriginalHtml;
      const accessibleLabel = btn.textContent.trim();
      if (btn.getAttribute("aria-label") !== accessibleLabel) btn.setAttribute("aria-label", accessibleLabel);
    });
  };
  const repaint = () => { paintAll(); syncCheckoutLabels(); };
  document.addEventListener("change", (e) => {
    const root = e.target.closest("[data-lux-cart-plans]");
    if (!root) return;
    const d = dataOf(root) || { reg: 0 };
    const { maxMonths } = planFor(d.reg);
    if (e.target.matches("[data-lux-cart-mode-input]")) {
      const hint = readHint();
      /* Snap to this basket's offered terms (final review minor 3): a stale or
         cash-split hint must not leak out-of-range months into GTM/storage. */
      const hinted = hint && INST_TERMS.includes(hint.months) && hint.months <= maxMonths ? hint.months : 0;
      const months = hinted || maxMonths;
      /* Switching modes resets cash to the plain 1-payment state; inst keeps
         (clamped) months. Cash split is chosen with its own 2/3 pills after. */
      saveHint(e.target.value, e.target.value === "cash" ? 1 : months);
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: "lux_cart_plan_selected", plan_type: e.target.value, months: e.target.value === "inst" ? months : 1 });
      repaint();
    } else if (e.target.name && e.target.name.indexOf("lux-cart-months-") === 0) {
      const months = parseInt(e.target.value, 10);
      saveHint("inst", months);
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: "lux_cart_plan_selected", plan_type: "inst", months });
      repaint();
    } else if (e.target.name && e.target.name.indexOf("lux-cart-cash-") === 0) {
      const n = parseInt(e.target.value, 10);
      saveHint("cash", n);
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: "lux_cart_plan_selected", plan_type: "cash", months: n });
      repaint();
    }
  });
  document.addEventListener("click", (e) => {
    const apply = e.target.closest("[data-lux-cart-apply]");
    if (!apply) return;
    const root = apply.closest("[data-lux-cart-plans]");
    const d = root && dataOf(root);
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: "lux_cart_application_click", items: d ? d.count : 0, total: d ? d.reg : 0 });
  });
  /* GA4 `view_cart` had NO source and read zero every day: its trigger group waits
     on `cart_viewed_stape`, which Shopify fires only on the /cart PAGE, and Lux
     ships a drawer. Measured 2026-08-02: zero views on any cart path in 9 full
     days against 325 on `/`, so the step never fired once. The theme already
     announces the drawer on document (cart-drawer.js toggleDrawerClasses), so
     that is the hook; no theme-core edit needed.
     The event NAME is deliberately the existing `cart_viewed_stape` so NO GTM
     change and NO container publish are required: trigger 43 already listens for
     it. It reads as a Stape-sourced name and is not one, hence this note.
     `/cart.js` is the item source because the inline cart JSON carries totals
     only, and only on instalment-eligible baskets. Its `total_price` is the CASH
     price, which is what checkout charges and what `purchase` reports, so the two
     events agree. The payload is products and prices only, never personal data. */
  let cartViewSent = false;
  document.addEventListener("cart-drawer-open", () => {
    if (cartViewSent) return;
    fetch(((window.routes && window.routes.cart_url) || "/cart") + ".js", { headers: { Accept: "application/json" } })
      .then((r) => (r.ok ? r.json() : null))
      .then((cart) => {
        /* The flag is burned only on a real push: an empty basket or a failed
           fetch must not block the view that follows the first add-to-cart. */
        if (!cart || !cart.item_count || cartViewSent) return;
        cartViewSent = true;
        window.dataLayer = window.dataLayer || [];
        /* Google's own instruction for ecommerce pushes: the dataLayer merges
           objects, so without this the `view_item` payload from the product page
           the visitor just left would leak its items into this cart event. */
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({
          event: "cart_viewed_stape",
          ecommerce: {
            currency: cart.currency,
            value: cart.total_price / 100,
            items: (cart.items || []).map((item, index) => ({
              item_id: String(item.variant_id),
              item_name: item.product_title,
              price: item.final_price / 100,
              quantity: item.quantity,
              index,
            })),
          },
        });
      })
      .catch(() => { /* tracking never breaks the drawer */ });
  });
  /* ---- Which VARIANT was really added, for Meta's product catalogue ----------
     Meta keys its catalogue by VARIANT id, but the Shopify pixel's
     ecommerce.items carry only item_id, which is the PRODUCT id (read live
     2026-08-02). GTM rebuilds the variant from
     ShopifyAnalytics.meta.selectedVariantId, which exists only for the product
     a product page is about, so a quick-add from a homepage card or a
     recommendation card cannot be resolved and falls back to a product id the
     catalogue never matches. product-form.js already publishes the add response
     for the PDP button AND for quick-add (which clones a real product form), so
     this reads what is already there rather than adding another cart listener.
     KEYED BY PRODUCT ID ON PURPOSE: a lookup can then only ever return a
     variant OF THAT SAME PRODUCT, so a stale entry is harmless by construction
     and can never tag one product with another product's variant. That property
     is what makes the GTM side safe to read it on any event. */
  window.luxAddedVariants = window.luxAddedVariants || {};
  /* Guarded on purpose. constants.js and pubsub.js are deferred in the head and
     this file is deferred in the body, so they always run first, but a bare
     reference would throw a ReferenceError if that ever stopped being true, and
     this IIFE also drives the whole cart plan price UI. A missing recorder costs
     Meta one id; a thrown ReferenceError costs the basket its prices. */
  if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS === "object") {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (payload) => {
      if (!payload || payload.source !== "product-form") return;
      const data = payload.cartData;
      /* /cart/add.js returns the line item alone, or {items:[...]} when
         `sections` is requested, which product-form.js does. Both shapes. */
      const lines = data && data.items ? data.items : [data];
      lines.forEach((line) => {
        if (line && line.product_id && line.id) {
          window.luxAddedVariants[String(line.product_id)] = String(line.id);
        }
      });
    });
  }
  /* The drawer (and the cart page js-contents) get innerHTML-swapped on cart
     changes; fresh server HTML is cash-default, so re-apply the stored state.
     Single-flight: both observers can fire several batches per frame during a
     swap; one queued repaint per frame covers all of them. */
  const observed = [];
  let queued = false;
  const schedule = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => { queued = false; repaint(); });
  };
  const observe = () => {
    [document.querySelector("cart-drawer"), document.getElementById("main-cart-footer")].forEach((el) => {
      if (!el || observed.includes(el)) return;
      observed.push(el);
      new MutationObserver(schedule).observe(el, { childList: true, subtree: true });
    });
  };
  const init = () => { observe(); repaint(); };
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
})();
